import React, { useState, useEffect, useRef, useCallback } from 'react';
import Regex from '../../utils/regex';
import { Icon, Input, message, Switch } from 'antd';
import {
  BorderedQuillContainer,
  Container,
  FieldTitle,
  OptionContainer,
  RowSection,
} from './styles';
import { useTranslation } from 'react-i18next';
import { COMBOBOX_SETTINGS_SIDEBAR, COMBOBOX_VALUES_TABLE_SETTINGS } from './translationValues';
import TextBlockWithQuillRenderer from '../../containers/ComplexDocumentDesign/TextBlockWithQuillRenderer';
import { constructAutoSaveJSON, constructQuillJSON } from '../../utils/TextBlockHelper';
import { FILLABLE_ELEMENTS_MAIN_CONTENT } from '../../artboard/Page/translationValues';
import { List, arrayMove } from 'react-movable';
import { deepCopy } from '../../utils/javascript';
import {
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
  useDocumentContext,
} from '../../context/complexDocumentContext';
import PageElementsProvider from '../../context/pageElementsContext';
import { useDefaultStyleContext } from '../../context/complexDefaultStyleContext';

export const dummyCallback = () => {};

const BorderedQuillRenderer = props => {
  const { uuid, contents, globalVariables = [], updateContent, canUpdateRandomKey, index } = props;
  const [textEditable, setTextEditable] = useState(false);
  const [randomKey, setRandomKey] = useState(1);

  useEffect(() => {
    if (canUpdateRandomKey) {
      setRandomKey(value => value + 1);
    }
  }, [contents]);

  return (
    <BorderedQuillContainer role="button">
      <div id={`combo-box-toolbar-${index}`} style={{ visibility: 'hidden', height: 0 }} />
      <div id={`combo-box-sidebar-variable-${index}`} style={{ position: 'relative', top: 10 }} />
      <div>
        <PageElementsProvider>
          <TextBlockWithQuillRenderer
            key={randomKey}
            defaultContent={constructQuillJSON(contents, 'Open Sans', 14, globalVariables)}
            saveBlock={updateContent}
            itemUUID={uuid}
            selected={true}
            enableSideBar={dummyCallback}
            enableAI={false}
            toolbarInvisible={true}
            adjustScroll={dummyCallback}
            textEditable={textEditable}
            setTextEditable={setTextEditable}
            toolbarDOM={`combo-box-toolbar-${index}`}
            variableDOM={`combo-box-sidebar-variable-${index}`}
            exposeRef={false}
          />
        </PageElementsProvider>
      </div>
    </BorderedQuillContainer>
  );
};

const SidebarOptionContainer = ({ deleteContent, ...props }) => {
  return (
    <OptionContainer>
      <Icon type="menu" data-movable-handle />
      <BorderedQuillRenderer {...props} />
      <Icon type="delete" onClick={deleteContent} role="button" />
    </OptionContainer>
  );
};

const NAMESPACES = ['COMBOBOX_SETTINGS_SIDEBAR'];
const defaultGetElementDetails = el => el.elementDetails;

const ComboBoxSettings = props => {
  const {
    element,
    onFieldNameChange,
    OPTIONS_KEY = 'validations',
    FIELD_NAME_KEY = 'fieldName',
    style = {},
    updateElementDetails,
    getElementDetails = defaultGetElementDetails,
    isComboboxGroup,
    isInteractiveDesign
  } = props;
  const { t } = useTranslation(NAMESPACES);
  const [canUpdateRandomKey, setCanUpdateRandomKey] = useState(true);

  const artboardSelector = useCallback(
    store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES],
    [],
  );
  const [globalVariables] = useDocumentContext(artboardSelector);

  const details = getElementDetails(element);

  const forwardRef = useRef(null);
  const fieldNameRef = useRef(null);
  const fieldName = details[OPTIONS_KEY][FIELD_NAME_KEY];

  useEffect(() => {
    if (forwardRef.current) {
      forwardRef.current = null;
    } else {
      if (fieldNameRef.current?.input)
        fieldNameRef.current.input.value = details[OPTIONS_KEY][FIELD_NAME_KEY];
    }
  }, [details]);

  const toggleVariable = checked => {
    updateElementDetails({ isVariable: checked }, 'style');
  };

  const updateComboboxValues = (contents, u, r, c, ref, op, newGlobalVariables = [], index) => {
    const newContents = constructAutoSaveJSON(contents, 'Open Sans', 14, newGlobalVariables);
    const comboBoxElement = deepCopy(element);
    comboBoxElement.contents[index].content = newContents;
    updateElementDetails(comboBoxElement.contents, 'contents');
  };

  const addNewContent = () => {
    const comboBoxElement = deepCopy(element);
    comboBoxElement.contents = [
      ...comboBoxElement.contents,
      {
        contentType: 'COMBO_BOX',
        content: [
          {
            contentType: 'PARAGRAPH',
            content: `${t(
              `FILLABLE_ELEMENTS_MAIN_CONTENT:${FILLABLE_ELEMENTS_MAIN_CONTENT.COMBO_BOX_TEXT.key}`,
              FILLABLE_ELEMENTS_MAIN_CONTENT.COMBO_BOX_TEXT.value,
            )} ${comboBoxElement?.contents?.length + 1}`,
            style: {
              fontSize: 14,
              color: '#000000',
            },
          },
          {
            contentType: 'LINE_BREAK',
            content: '\n',
            style: {
              fontSize: 14,
              lineSpacing: 1.5,
            },
          },
        ],
      },
    ];
    updateElementDetails(comboBoxElement.contents, 'contents');
  };

  const deleteContentAtIndex = index => {
    const comboBoxElement = deepCopy(element);
    const updatedContents = comboBoxElement.contents.filter((el, elIndex) => elIndex !== index);
    setCanUpdateRandomKey(true);
    updateElementDetails(updatedContents, 'contents');
    requestAnimationFrame(() => setCanUpdateRandomKey(false));
  };

  return (
    <Container
      style={style}
      onMouseEnter={() => setCanUpdateRandomKey(false)}
      onMouseLeave={() => setCanUpdateRandomKey(true)}
    >
      <FieldTitle isTitle={1}>
        {!isComboboxGroup
          ? t(
              COMBOBOX_SETTINGS_SIDEBAR.COMBO_BOX_SETTINGS_TITLE.key,
              COMBOBOX_SETTINGS_SIDEBAR.COMBO_BOX_SETTINGS_TITLE.value,
            )
          : t(
              COMBOBOX_SETTINGS_SIDEBAR.COMBO_BOX_GROUP_SETTINGS_TITLE.key,
              COMBOBOX_SETTINGS_SIDEBAR.COMBO_BOX_GROUP_SETTINGS_TITLE.value,
            )}
      </FieldTitle>
      <RowSection>
        <FieldTitle>
          {t(
            COMBOBOX_SETTINGS_SIDEBAR.FIELD_NAME_LABEL.key,
            COMBOBOX_SETTINGS_SIDEBAR.FIELD_NAME_LABEL.value,
          )}
          *
        </FieldTitle>
        <Input
          ref={fieldNameRef}
          placeholder={t(
            COMBOBOX_SETTINGS_SIDEBAR.FIELD_NAME_PLACEHOLDER.key,
            COMBOBOX_SETTINGS_SIDEBAR.FIELD_NAME_PLACEHOLDER.value,
          )}
          onChange={e => {
            if (Regex.NO_SPLCHAR_EXCEPT_HYPHEN.test(e.target.value)) {
              message.error(
                t(
                  COMBOBOX_SETTINGS_SIDEBAR.FIELD_NAME_SPECIAL_CHARACTER_ERROR_MESSAGE.key,
                  COMBOBOX_SETTINGS_SIDEBAR.FIELD_NAME_SPECIAL_CHARACTER_ERROR_MESSAGE.value,
                ),
              );
              e.target.value = details[OPTIONS_KEY][FIELD_NAME_KEY];
            } else {
              forwardRef.current = e.target;
              onFieldNameChange(e.target.value);
            }
          }}
          defaultValue={fieldName}
        />
      </RowSection>
      {!isComboboxGroup ? (
        <>
          <RowSection>
            <FieldTitle>
              {t(
                COMBOBOX_SETTINGS_SIDEBAR.IS_VARIABLE_LABEL.key,
                COMBOBOX_SETTINGS_SIDEBAR.IS_VARIABLE_LABEL.value,
              )}
            </FieldTitle>
            <Switch
              checkedChildren={t(
                COMBOBOX_SETTINGS_SIDEBAR.DYNAMIC_LABEL_YES_SWITCH.key,
                COMBOBOX_SETTINGS_SIDEBAR.DYNAMIC_LABEL_YES_SWITCH.value,
              )}
              unCheckedChildren={t(
                COMBOBOX_SETTINGS_SIDEBAR.DYNAMIC_LABEL_NO_SWITCH.key,
                COMBOBOX_SETTINGS_SIDEBAR.DYNAMIC_LABEL_NO_SWITCH.value,
              )}
              checked={element?.style?.isVariable}
              onChange={toggleVariable}
              disabled={isInteractiveDesign}
            />
          </RowSection>
          <RowSection isComboVal={1} id="combobox-option-row">
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <FieldTitle>
                {t(
                  COMBOBOX_SETTINGS_SIDEBAR.VALUES_LIST_LABEL.key,
                  COMBOBOX_SETTINGS_SIDEBAR.VALUES_LIST_LABEL.value,
                )}
              </FieldTitle>
              <FieldTitle
                style={{
                  justifyContent: 'flex-end',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
                onClick={addNewContent}
              >
                {t(
                  COMBOBOX_VALUES_TABLE_SETTINGS.ADD_A_VALUE_ACTION.key,
                  COMBOBOX_VALUES_TABLE_SETTINGS.ADD_A_VALUE_ACTION.value,
                )}
              </FieldTitle>
            </div>
            <List
              values={element?.contents}
              lockVertically
              container={document.getElementById('combobox-option-row')}
              renderList={({ children, props, isDragged }) => (
                <ul
                  {...props}
                  style={{ padding: 0, cursor: isDragged ? 'grabbing' : undefined, width: '100%' }}
                >
                  {children}
                </ul>
              )}
              renderItem={({ value, index, props, isDragged, isSelected }) => (
                <li
                  {...props}
                  tabIndex={-1}
                  style={{
                    ...props.style,
                    listStyleType: 'none',
                    zIndex: 99999 - index,
                  }}
                >
                  <SidebarOptionContainer
                    contents={value.content}
                    uuid={element.uuid}
                    globalVariables={globalVariables}
                    updateContent={(...rest) => updateComboboxValues(...rest, index)}
                    deleteContent={() => deleteContentAtIndex(index)}
                    index={index}
                    canUpdateRandomKey={canUpdateRandomKey}
                  />
                </li>
              )}
              onChange={({ oldIndex, newIndex }) => {
                setCanUpdateRandomKey(true);
                const comboBoxElement = JSON.parse(JSON.stringify(element));
                updateElementDetails(
                  arrayMove(comboBoxElement?.contents, oldIndex, newIndex),
                  'contents',
                );
                requestAnimationFrame(() => setCanUpdateRandomKey(false));
              }}
            />
          </RowSection>
        </>
      ) : null}
    </Container>
  );
};

export default ComboBoxSettings;
